import { useCachedReports, useWorkspaceStore } from '@easy-expense/data-firestore-client';
import { CategoryValue, Expense } from '@easy-expense/data-schema-v2';
import Data from '@easy-expense/frontend-data-layer';
import { getUserDisplayName, tipEligibleCategories } from '@easy-expense/utils-shared';
import _ from 'lodash';
import React from 'react';

import { ExpenseWithData } from './ExpenseRow.component';

export function isTipEligibleCategory(categoryValue?: CategoryValue | null): boolean {
  if (!categoryValue) {
    return false;
  }

  const categoryName = categoryValue.name ?? '';
  return (
    categoryValue.tipEligible ?? (tipEligibleCategories as readonly string[]).includes(categoryName)
  );
}

export function isTipEligibleCategoryKey(categoryKey?: string): boolean {
  if (!categoryKey) {
    return false;
  }

  return isTipEligibleCategory(Data.expenseCategories.getByKey(categoryKey)?.value);
}

export const useMappedExpenseData = (expenseKeys?: string[]): ExpenseWithData[] => {
  const expenses = Data.expenses.use();
  const vendors = Data.vendors.use();
  const categories = Data.expenseCategories.use();
  const paymentMethods = Data.paymentMethods.use();
  const members = useWorkspaceStore((s) => s.workspaceMembers);
  const reports = useCachedReports();

  return React.useMemo(() => {
    return expenses
      .filter((e) => {
        if (expenseKeys) {
          return expenseKeys?.includes(e.key);
        }
        return true;
      })
      .map((e) => {
        // A deleted vendor, category or payment method is no longer in the selectable lists, but
        // the expense still points at it. Fall back to a lookup by key so the row keeps reading
        // the way it was filed instead of going blank, the same as it does on mobile.
        const vendorValue = (
          vendors?.find((v) => v.key === e.vendor) ?? Data.vendors.getByKey(e.vendor)
        )?.value;
        const categoryValue = (
          categories?.find((c) => c.key === e.category) ??
          Data.expenseCategories.getByKey(e.category)
        )?.value;
        const paymentMethodValue = (
          paymentMethods?.find((pm) => pm.key === e.paymentMethod) ??
          Data.paymentMethods.getByKey(e.paymentMethod)
        )?.value;
        const member = members[e.createdBy];
        const creatorDisplayName = getUserDisplayName(member);
        const reportsToAdd = Object.values(reports)?.filter((r) => r.expenses?.includes(e.key));

        return {
          ...e,
          vendorValue,
          categoryValue,
          paymentMethodValue,
          vendorValueName: vendorValue?.name ?? '',
          paymentMethodName: paymentMethodValue?.name ?? '',
          categoryValueName: categoryValue?.name ?? '',
          creatorDisplayName,
          reports: reportsToAdd,
        };
      });
  }, [expenses, vendors, categories, paymentMethods, reports, members]);
};

export const applyFilters = (
  expenses: ExpenseWithData[],
  filter: (e: Expense) => boolean,
  filterUser?: string,
): ExpenseWithData[] => {
  return expenses.filter((e) => {
    if (!filter(e)) {
      return false;
    }
    if (filterUser && e.createdBy !== filterUser) {
      return false;
    }
    return true;
  });
};

export const useSearchExpenseList = (
  expenses: ExpenseWithData[],
  search?: string,
): ExpenseWithData[] => {
  const terms = [
    'vendorValue.name',
    'categoryValue.name',
    'desc',
    'total',
    'paymentMethodValue.name',
  ];

  return React.useMemo(() => {
    if (search) {
      return expenses.filter((listEntity) => {
        const entityValues = terms.map((term) => _.get(listEntity, term));

        const searchValues = entityValues.reduce((acc, entityValue) => {
          if (entityValue) {
            entityValue = typeof entityValue === 'string' ? entityValue : entityValue.toString();
            acc.push(entityValue.toLowerCase());
          }
          return acc;
        }, []);

        const expenseValuesString = searchValues.join(' ');
        return expenseValuesString.includes(search.toLowerCase());
      });
    } else {
      return expenses;
    }
  }, [expenses, search]);
};
